import { useEffect, useState } from "react";
import {
  startRegistration,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { Fingerprint, Trash2, KeyRound, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/webauthn/credentials");
      setCredentials(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const registerPasskey = async () => {
    setErr("");
    setNotice("");
    if (!browserSupportsWebAuthn()) {
      setErr("Dein Browser unterstützt keine Passkeys.");
      return;
    }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/auth/webauthn/register/begin");
      const attestation = await startRegistration({ optionsJSON: opts });
      await api.post("/auth/webauthn/register/complete", {
        credential: attestation,
        label: label || navigator.userAgent.slice(0, 40),
      });
      setNotice("Passkey registriert. Du kannst dich jetzt damit einloggen.");
      setLabel("");
      load();
    } catch (e) {
      setErr(formatApiError(e) || "Registrierung abgebrochen");
    } finally {
      setBusy(false);
    }
  };

  const deletePasskey = async (id) => {
    if (!window.confirm("Passkey wirklich entfernen?")) return;
    try {
      await api.delete(`/auth/webauthn/credentials/${id}`);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setErr("");
    setNotice("");
    if (newPassword !== newPassword2) {
      setErr("Passwörter stimmen nicht überein.");
      return;
    }
    if (newPassword.length < 8) {
      setErr("Neues Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    setPwSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setNotice("Passwort erfolgreich geändert.");
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setPwSubmitting(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Einstellungen" subtitle="// account" testId="settings-header" />

      {err ? (
        <div className="mx-8 mt-4 flex items-start gap-2 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.5} />
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#10b981] border border-[#10b981]/40 bg-[#10b981]/5 px-3 py-2">
          {notice}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1f1f22] m-8">
        {/* ACCOUNT */}
        <div className="bg-[#0a0a0a] p-6">
          <div className="overline mb-4">// account</div>
          <div className="grid grid-cols-[120px_1fr] gap-2 text-sm font-mono">
            <div className="text-[#8b8d98]">User:</div>
            <div className="text-white">{user?.username}</div>
            <div className="text-[#8b8d98]">Rolle:</div>
            <div className="text-white">admin</div>
          </div>

          <form onSubmit={changePassword} data-testid="change-password-form" className="mt-8 space-y-4">
            <div className="overline flex items-center gap-2">
              <KeyRound className="w-3 h-3" strokeWidth={1.5} /> Passwort ändern
            </div>
            <div>
              <label className="overline block mb-1">Aktuelles Passwort</label>
              <input
                data-testid="current-password-input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-black border border-[#1f1f22] text-white font-mono px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
              />
            </div>
            <div>
              <label className="overline block mb-1">Neues Passwort</label>
              <input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-black border border-[#1f1f22] text-white font-mono px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
              />
            </div>
            <div>
              <label className="overline block mb-1">Wiederholen</label>
              <input
                data-testid="new-password-confirm-input"
                type="password"
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                className="w-full bg-black border border-[#1f1f22] text-white font-mono px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
              />
            </div>
            <button
              type="submit"
              disabled={pwSubmitting}
              data-testid="change-password-submit"
              className="w-full flex items-center justify-center gap-2 border border-[#22d3ee] bg-[#22d3ee]/10 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black transition-colors py-2 text-xs font-mono tracking-[0.2em] uppercase disabled:opacity-50"
            >
              {pwSubmitting ? "Speichere…" : "Passwort speichern"}
            </button>
          </form>
        </div>

        {/* PASSKEYS */}
        <div className="bg-[#0a0a0a] p-6">
          <div className="overline mb-4 flex items-center gap-2">
            <Fingerprint className="w-3 h-3" strokeWidth={1.5} /> Passkeys (WebAuthn)
          </div>
          <p className="text-xs font-mono text-[#8b8d98] mb-4 leading-relaxed">
            Registriere einen Passkey (Fingerabdruck, Face-ID, Hardware-Key,
            Android/iOS-Geräte) und logge dich anschließend ohne Passwort ein.
          </p>

          <div className="flex gap-2 mb-4">
            <input
              data-testid="passkey-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (optional, z.B. MacBook)"
              className="flex-1 bg-black border border-[#1f1f22] text-white font-mono px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
            />
            <button
              onClick={registerPasskey}
              disabled={busy}
              data-testid="passkey-register-button"
              className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black disabled:opacity-50"
            >
              <Fingerprint className="w-3.5 h-3.5" strokeWidth={1.5} />
              {busy ? "Warte…" : "Registrieren"}
            </button>
          </div>

          <div className="divide-y divide-[#1f1f22]">
            {credentials.map((c) => (
              <div
                key={c.id}
                data-testid={`passkey-row-${c.id}`}
                className="flex items-center gap-3 py-3 font-mono text-[11px]"
              >
                <Fingerprint className="w-4 h-4 text-[#22d3ee]" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="text-white">{c.label || "Passkey"}</div>
                  <div className="text-[#8b8d98]">
                    Erstellt: {new Date(c.created_at).toLocaleString()}
                    {c.last_used_at ? ` · Zuletzt: ${new Date(c.last_used_at).toLocaleString()}` : ""}
                    {c.rp_id ? ` · ${c.rp_id}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => deletePasskey(c.id)}
                  data-testid={`passkey-delete-${c.id}`}
                  className="text-[#dc2626] hover:text-white"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
            {!credentials.length && (
              <div className="text-xs font-mono text-[#8b8d98] py-6">
                Noch keine Passkeys registriert.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
